// Angemeldete Geräte — in den Einstellungen.
//
// Drei Dinge stehen hier: welche Geräte dauerhaft angemeldet sind (Handy,
// Desktop-App), in welchen Browsern gerade jemand angemeldet ist, und ob
// dieses Gerät nach Fingerabdruck fragen soll.
//
// Abmelden wirkt SOFORT — der Server beendet die laufende Sitzung mit. Wer
// die eigene Anmeldung beendet, landet gleich auf der Anmeldeseite.
//
// Das Abmelden aus der Ferne ist der wichtigere Teil. Wer sein Telefon
// verliert, muss den Zugang von einem anderen Gerät aus beenden können, ohne
// auf Hilfe zu warten — und ein Administrator muss das für sein Team tun
// können, wenn jemand ausscheidet.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { datumZeit } from '../../../gemeinsam/lib/daten';
import { istNativ } from '../../../gemeinsam/lib/plattform';
import { biometrieAn, biometrieArt, biometrieSetzen, type BiometrieArt } from '../lib/geraet';
import { Abzeichen, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Geraet = {
  id: string; name: string; plattform: string; modell: string; version: string;
  angemeldetAm: string; zuletztGesehen: string; laeuftAbAm?: string | null; diesesGeraet: boolean;
  benutzer?: string; eigenes: boolean;
};

type Sitzung = {
  id: string; name: string; angemeldetAm: string; zuletztGesehen: string; laeuftAbAm: string;
  dieseSitzung: boolean; benutzer?: string; eigenes: boolean;
};

const ART_TEXT: Record<BiometrieArt, string> = {
  faceId: 'Face ID', touchId: 'Touch ID', fingerabdruck: 'Fingerabdruck',
  andere: 'Gerätesperre', keine: '',
};

export default function Geraete() {
  const { t } = useSprache();
  const { geraetGemerkt, abmelden: selbstAbmelden } = useAuth();
  const [liste, setListe] = useState<Geraet[] | null>(null);
  const [sitzungen, setSitzungen] = useState<Sitzung[]>([]);
  const [sichtAlle, setSichtAlle] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [art, setArt] = useState<BiometrieArt>('keine');
  const [sperreAn, setSperreAn] = useState(biometrieAn());

  const laden = useCallback(async () => {
    try {
      const d = await api<{ geraete: Geraet[]; sitzungen?: Sitzung[]; sichtAlle: boolean }>('/auth/geraete');
      setListe(d.geraete);
      setSitzungen(d.sitzungen ?? []);
      setSichtAlle(d.sichtAlle);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : t('geraete.ladefehler'));
      setListe([]);
    }
  }, [t]);

  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { void biometrieArt().then(setArt); }, []);

  /** Gerät oder Browser-Anmeldung beenden. Die eigene → sofort hier abmelden. */
  async function beenden(art: 'geraete' | 'sitzungen', id: string, eigeneAnmeldung: boolean) {
    // Die eigene Anmeldung: genau wie „Abmelden" — der Server beendet sie samt
    // Gerät, die Anwesenheit springt auf offline, man landet bei der Anmeldung.
    if (eigeneAnmeldung) { selbstAbmelden(); return; }
    setBeschaeftigt(id);
    setFehler(null);
    try {
      await api(`/auth/${art}/${id}`, { methode: 'DELETE' });
      await laden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : t('geraete.abmeldenFehler'));
    } finally { setBeschaeftigt(null); }
  }

  /** Alle eigenen Anmeldungen außer dieser — etwa nach einem verlorenen Telefon. */
  async function alleAnderen() {
    setBeschaeftigt('alle');
    setFehler(null);
    try {
      for (const g of (liste ?? []).filter(x => x.eigenes && !x.diesesGeraet)) await api(`/auth/geraete/${g.id}`, { methode: 'DELETE' });
      for (const s of sitzungen.filter(x => x.eigenes && !x.dieseSitzung)) await api(`/auth/sitzungen/${s.id}`, { methode: 'DELETE' });
      await laden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : t('geraete.abmeldenFehler'));
    } finally { setBeschaeftigt(null); }
  }

  const andereEigene = (liste ?? []).filter(x => x.eigenes && !x.diesesGeraet).length
    + sitzungen.filter(x => x.eigenes && !x.dieseSitzung).length;

  return (
    <div className="space-y-4">
      {/* Biometrie nur dort anbieten, wo es sie gibt. */}
      {istNativ() && art !== 'keine' && (
        <Karte>
          <KartenKopf titel={t('geraete.sperreTitel')} hinweis={ART_TEXT[art]} />
          <div className="p-5">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" checked={sperreAn}
                     onChange={e => { setSperreAn(e.target.checked); biometrieSetzen(e.target.checked); }}
                     className="mt-0.5 h-4 w-4 rounded border-slate-300 text-marke-600" />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                {t('geraete.sperreAn').replace('{art}', ART_TEXT[art])}
                <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                  {t('geraete.sperreText')}
                </span>
              </span>
            </label>
            {!sperreAn && (
              <div className="mt-3"><Hinweis art="warnung">{t('geraete.sperreAusWarnung')}</Hinweis></div>
            )}
          </div>
        </Karte>
      )}

      <Karte>
        <KartenKopf
          titel={t('geraete.titel')}
          hinweis={sichtAlle ? t('geraete.hinweisAdmin') : t('geraete.hinweis')}
          aktion={andereEigene > 0 && (
            <Knopf variante="still" className="!text-xs" laedt={beschaeftigt === 'alle'} onClick={alleAnderen}>
              {t('geraete.alleAnderen')}
            </Knopf>
          )}
        />

        {fehler && <div className="p-5 pb-0"><Hinweis art="fehler">{fehler}</Hinweis></div>}

        {liste === null ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : liste.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('geraete.keine')}
          </p>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(g => (
              <li key={g.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900 dark:text-slate-100">{g.name}</span>
                    {g.diesesGeraet && <Abzeichen art="gruen">{t('geraete.dieses')}</Abzeichen>}
                    {g.benutzer && !g.eigenes && <Abzeichen>{g.benutzer}</Abzeichen>}
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                    {[g.modell, g.version && `Version ${g.version}`].filter(Boolean).join(' · ')}
                    {g.modell || g.version ? ' · ' : ''}
                    {t('geraete.zuletzt')} {datumZeit(g.zuletztGesehen)}
                    {g.laeuftAbAm && <> · {t('geraete.gueltigBis').replace('{datum}', datumZeit(g.laeuftAbAm))}</>}
                  </span>
                </span>
                <Knopf variante="gefahr" className="!text-xs" laedt={beschaeftigt === g.id}
                       onClick={() => beenden('geraete', g.id, g.diesesGeraet)}>
                  {t('geraete.abmelden')}
                </Knopf>
              </li>
            ))}
          </ul>
        )}

        {/* Browser-Anmeldungen: laufen höchstens 24 Stunden, lassen sich einzeln beenden. */}
        {sitzungen.length > 0 && (
          <div className="border-t border-slate-200 dark:border-slate-800">
            <p className="px-5 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('geraete.browserTitel')}
            </p>
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {sitzungen.map(s => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900 dark:text-slate-100">{s.name}</span>
                      {s.dieseSitzung && <Abzeichen art="gruen">{t('geraete.diese')}</Abzeichen>}
                      {s.benutzer && !s.eigenes && <Abzeichen>{s.benutzer}</Abzeichen>}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                      {t('geraete.zuletzt')} {datumZeit(s.zuletztGesehen)}
                    </span>
                  </span>
                  <Knopf variante="gefahr" className="!text-xs" laedt={beschaeftigt === s.id}
                         onClick={() => beenden('sitzungen', s.id, s.dieseSitzung)}>
                    {t('geraete.abmelden')}
                  </Knopf>
                </li>
              ))}
            </ul>
            <p className="px-5 pb-4 pt-1 text-xs text-slate-500 dark:text-slate-400">{t('geraete.browserHinweis')}</p>
          </div>
        )}

        {istNativ() && !geraetGemerkt && (
          <div className="p-5 pt-0">
            <Hinweis art="info">{t('geraete.nichtGemerkt')}</Hinweis>
          </div>
        )}
      </Karte>
    </div>
  );
}
